/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import StaffTimeOff from '../components/StaffTimeOff';
import { toET, todayET } from '../utils/et';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';

const user = JSON.parse(localStorage.getItem('nemt_user') || '{}');
const isAdmin = user.role === 'admin';

const STATUS_COLORS = {
  pending:  { bg: '#FAEEDA', color: '#854F0B', label: 'Pending' },
  approved: { bg: '#E1F5EE', color: '#0F6E56', label: 'Approved' },
  denied:   { bg: '#FCEBEB', color: '#A32D2D', label: 'Denied' },
};

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function calcHours(startTime, endTime) {
  if (!startTime || !endTime) return null;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) return null;
  return (mins / 60).toFixed(2);
}

function EntryModal({ onClose, onSubmit, existingDates }) {
  const today = todayET();
  const [form, setForm] = useState({ date: today, startTime: '', endTime: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hours = calcHours(form.startTime, form.endTime);

  const handleSubmit = async () => {
    if (!form.date || !form.startTime || !form.endTime) {
      setError('Please fill in date, start time, and end time');
      return;
    }
    if (!hours) {
      setError('End time must be after start time');
      return;
    }
    setSaving(true);
    try {
      await onSubmit(form, hours);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not submit — try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 420, padding: '1.5rem' }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 16, marginTop: 0 }}>Log hours</h2>

        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 10px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Date</label>
          <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Start time</label>
            <input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>End time</label>
            <input type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }} />
          </div>
        </div>

        {hours && (
          <div style={{ background: '#E1F5EE', color: '#0F6E56', padding: '6px 10px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
            {hours} hours
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={2} placeholder="What did you work on?"
            style={{ width: '100%', resize: 'none', padding: '8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }} />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, fontSize: 13, padding: '10px', border: '1px solid #ddd', borderRadius: 8, background: 'white', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ flex: 2, fontSize: 13, padding: '10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            {saving ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyHours() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);

  // Admin: pending entries from all dispatchers
  const [pendingEntries, setPendingEntries] = useState([]);

  const now = toET(new Date());
  const weekStart = startOfWeek(addWeeks(now, weekOffset), { weekStartsOn: 1 });
  const weekEnd = endOfWeek(addWeeks(now, weekOffset), { weekStartsOn: 1 });
  const weekLabel = `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`;

  const fetchEntries = useCallback(async () => {
    try {
      const dateStr = format(weekStart, 'yyyy-MM-dd');
      const res = await api.get(`/payroll/dispatcher-work-log?userEmail=${encodeURIComponent(user.email)}&weekOf=${dateStr}`);
      setEntries(res.data.entries || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [weekOffset]);

  const fetchPending = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/payroll/dispatcher-work-log?status=pending');
      setPendingEntries(res.data.entries || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleSubmit = async (form, hours) => {
    await api.post('/payroll/dispatcher-work-log', {
      userEmail: user.email,
      personName: `${user.firstName} ${user.lastName}`,
      workDate: form.date,
      startTime: form.startTime,
      endTime: form.endTime,
      hours,
      notes: form.notes || null,
    });
    setMessage('Hours submitted!');
    setTimeout(() => setMessage(''), 3000);
    fetchEntries();
  };

  const handleReview = async (entryId, status) => {
    await api.put(`/payroll/dispatcher-work-log/${entryId}`, {
      status,
      reviewedBy: `${user.firstName} ${user.lastName}`,
    });
    fetchPending();
    fetchEntries();
  };

  const handleDelete = async (entryId) => {
    await api.delete(`/payroll/dispatcher-work-log/${entryId}`);
    fetchEntries();
  };

  const approvedHours = entries.filter(e => e.status === 'approved').reduce((sum, e) => sum + parseFloat(e.hours || 0), 0);
  const pendingHours = entries.filter(e => e.status === 'pending').reduce((sum, e) => sum + parseFloat(e.hours || 0), 0);
  const totalHours = entries.reduce((sum, e) => sum + parseFloat(e.hours || 0), 0);

  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>My hours</h1>
        <button onClick={() => setShowModal(true)}
          style={{ fontSize: 13, background: '#185FA5', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer' }}>
          + Log hours
        </button>
      </div>

      {message && (
        <div style={{ background: '#E1F5EE', color: '#0F6E56', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {message}
        </div>
      )}

      {/* Week navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button onClick={() => setWeekOffset(w => w - 1)}
          style={{ fontSize: 13, padding: '6px 12px', border: '1px solid #ddd', borderRadius: 6, background: 'white', cursor: 'pointer' }}>
          Previous
        </button>
        <span style={{ fontSize: 14, fontWeight: 500 }}>{weekLabel}</span>
        <button onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0}
          style={{ fontSize: 13, padding: '6px 12px', border: '1px solid #ddd', borderRadius: 6, background: 'white', cursor: 'pointer', opacity: weekOffset >= 0 ? 0.4 : 1 }}>
          Next
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 20 }}>
        <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Total hours</div>
          <div style={{ fontSize: 22, fontWeight: 500 }}>{totalHours.toFixed(1)}h</div>
        </div>
        <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Approved</div>
          <div style={{ fontSize: 22, fontWeight: 500, color: '#1D9E75' }}>{approvedHours.toFixed(1)}h</div>
        </div>
        <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Pending</div>
          <div style={{ fontSize: 22, fontWeight: 500, color: pendingHours > 0 ? '#BA7517' : '#666' }}>{pendingHours.toFixed(1)}h</div>
        </div>
      </div>

      {/* Estimated pay */}
      <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '12px 14px', marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Estimated pay this week</div>
        <div style={{ fontSize: 22, fontWeight: 500 }}>${(approvedHours * 25).toFixed(2)}</div>
        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{approvedHours.toFixed(1)} approved hours x $25.00/hr</div>
      </div>

      {/* Daily entries */}
      <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 10, color: '#666' }}>This week's entries</h2>

      {loading ? (
        <div style={{ color: '#999', fontSize: 13, textAlign: 'center', padding: '2rem' }}>Loading...</div>
      ) : entries.length === 0 ? (
        <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#999', fontSize: 13 }}>
          No hours logged this week — tap "+ Log hours" to add an entry
        </div>
      ) : (
        entries.map(entry => {
          const status = STATUS_COLORS[entry.status] || STATUS_COLORS.pending;
          const dateStr = entry.work_date ? String(entry.work_date).substring(0, 10) : null;
          const dateParts = dateStr ? dateStr.split('-') : null;
          const date = dateParts ? new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2])) : null;
          const dateLabel = date ? format(date, 'EEE MMM d') : '--';

          return (
            <div key={entry.id} style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{dateLabel}</div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                    {formatTime(entry.start_time)} – {formatTime(entry.end_time)}
                    {entry.hours && ` · ${parseFloat(entry.hours).toFixed(2)}h`}
                  </div>
                  {entry.notes && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{entry.notes}</div>}
                  {entry.admin_response && (
                    <div style={{ fontSize: 11, color: entry.status === 'denied' ? '#A32D2D' : '#0F6E56', marginTop: 4, fontStyle: 'italic' }}>
                      "{entry.admin_response}"
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ background: status.bg, color: status.color, fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 500 }}>
                    {status.label}
                  </span>
                  {entry.status === 'pending' && (
                    <button onClick={() => handleDelete(entry.id)}
                      style={{ fontSize: 11, color: '#999', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* Own days off (Paula 2026-10-20) */}
      <StaffTimeOff mode="mine" />

      {/* Admin: pending approvals from all dispatchers */}
      {isAdmin && pendingEntries.length > 0 && (
        <>
          <h2 style={{ fontSize: 14, fontWeight: 500, marginTop: 32, marginBottom: 10, color: '#666' }}>Pending dispatcher approvals</h2>
          {pendingEntries.map(entry => {
            const dateStr = entry.work_date ? String(entry.work_date).substring(0, 10) : null;
            const dateParts = dateStr ? dateStr.split('-') : null;
            const date = dateParts ? new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2])) : null;
            const dateLabel = date ? format(date, 'EEE MMM d') : '--';

            return (
              <div key={entry.id} style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{entry.person_name || entry.user_email}</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      {dateLabel} · {formatTime(entry.start_time)} – {formatTime(entry.end_time)} · {parseFloat(entry.hours).toFixed(2)}h
                    </div>
                    {entry.notes && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{entry.notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => handleReview(entry.id, 'approved')}
                      style={{ fontSize: 12, padding: '6px 12px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                      Approve
                    </button>
                    <button onClick={() => handleReview(entry.id, 'denied')}
                      style={{ fontSize: 12, padding: '6px 12px', background: '#E24B4A', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                      Deny
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {showModal && <EntryModal onClose={() => setShowModal(false)} onSubmit={handleSubmit} />}
    </div>
  );
}
